/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Inject, inject} from '@angular/core';
import template from './coupon-edit-page.component.html';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {parseExpiryFields, validityCalculation} from './coupon-commons';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {CouponApiService, CouponModel} from './coupon-api.service';
import {CouponProductSearchState} from './coupon-product-search-state';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import {AppSharedModule} from '../../common/app-shared.module';
import {AdnSelectComponent} from '../../common/directives/adn-select/adn-select.component';
import {FormControlsModule} from '../../common/directives/form-controls/form-controls.module';
import {CouponValidityComponent} from './coupon-validity-component';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, TranslateModule, ButtonsModule, AdnSelectComponent, FormControlsModule, CouponValidityComponent],
  selector: 'adn-coupon-edit-page',
  template
})
export class CouponEditPageComponent extends SignalAdminResourceEditPageBase<CouponModel> {
  readonly expiryUnits = ['Days', 'Months', 'Years'];

  readonly productSearch: CouponProductSearchState;

  constructor(
    couponApi: CouponApiService,
    @Inject(LocalNetworkProfile) private readonly networkProfile: any
  ) {
    super(couponApi, 'coupon');
    this.productSearch = new CouponProductSearchState(couponApi, this.componentDestroyRef);
  }

  private readonly componentDestroyRef = inject(DestroyRef);

  isClaimableAt(value: string): boolean {
    return (this.editingModel.claimableAts || []).includes(value);
  }

  onClaimableChange(value: string, event: Event): void {
    this.setClaimableAt(value, (event.target as HTMLInputElement).checked);
  }

  setClaimableAt(value: string, checked: boolean): void {
    let claimableAts = [...(this.editingModel.claimableAts || [])];
    if (checked && !claimableAts.includes(value)) {
      claimableAts.push(value);
    }
    if (!checked) {
      claimableAts = claimableAts.filter((entry) => entry !== value);
    }
    this.editingModel.claimableAts = claimableAts;
    this.markExternalFormDirty();
  }

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  protected override initialiseModel(model: CouponModel): CouponModel {
    const initialised = super.initialiseModel(model);
    if (this.isNew && !initialised.validFrom) {
      const networkTimezone = this.networkProfile.get('timeZone') || 'utc';
      initialised.validFrom = moment().tz(networkTimezone).startOf('day').toDate();
      initialised.validTo = moment().tz(networkTimezone).add(30, 'day').endOf('day').toDate();
    }

    initialised.type = initialised.type || 'MONETARY';
    initialised.couponStatus = initialised.couponStatus || 'PENDING';
    initialised.oneTimeValidity = initialised.oneTimeValidity ?? true;
    initialised.allProducts = _.get(initialised, ['products'], []).length < 1;
    initialised.couponEndDateType = initialised.couponEndDateType || 'VALID_TO';
    initialised.claimableAts = initialised.claimableAts || ['CAMPAIGN_CREATION'];

    if (initialised.validFrom) {
      initialised.modValidFrom = initialised.validFrom;
    }
    if (initialised.validTo) {
      initialised.modValidTo = initialised.validTo;
    }

    parseExpiryFields(initialised);
    validityCalculation(initialised);
    return initialised;
  }

  protected override prepareForSubmit(model: CouponModel): void {
    model.validFrom = _.isDate(model.modValidFrom) ? model.modValidFrom : null;
    model.validTo = _.isDate(model.modValidTo) ? model.modValidTo : null;
    model.expiry = model.expiryNumber > 0 && model.expiryUnit
      ? 'P' + model.expiryNumber + model.expiryUnit.charAt(0) : null;

    if (model.allProducts) {
      model.products = [];
    }
  }

  protected override afterSave(model: CouponModel): void {
    model.allProducts = _.get(model, ['products'], []).length < 1;
    validityCalculation(model);
  }
}
